<template>
  <div ref="container" :class="[_class, _sizeClass]">
    <label v-if="labelMenu" :for="menuButtonId" class="inline-label">{{
      labelMenu
    }}</label>
    <a
      :id="menuButtonId"
      ref="button"
      role="button"
      tabindex="0"
      :aria-haspopup="html && !items ? 'true' : 'menu'"
      :aria-expanded="open ? 'true' : null"
      :aria-label="descriptiveLabel"
      :aria-live="triggerAriaLive ? triggerAriaLive : null"
      :aria-controls="html && !items ? resolvedId + '-menuWrap' : null"
      class="cat-menu-button"
      :class="[triggerClass, buttonCss]"
      :data-label="label || current || offscreenLabel"
      @click.stop="toggle"
      @keydown.enter.stop="toggle"
      @keydown.space.prevent="toggle"
      @keydown="onKeyDown"
      @keydown.esc="close($event, true)"
      :aria-describedby="describedBy ? describedBy : undefined"
    >
      <div class="cat-menu-button-inner">
        <cat-icon
          v-if="icon"
          class="cat-menu-icon-left"
          :icon="icon"
          :decorative="true"
        />
        <span class="trigger" role="presentation">
          <slot name="trigger">{{ label ? label : current }}</slot>
        </span>
        <em
          v-if="!hideDefaultArrow"
          aria-hidden="true"
          class="cat-inline-menu-caret icon-down-dir"
        ></em>
        <cat-icon
          v-if="iconRight"
          class="cat-menu-icon-right"
          :icon="iconRight"
          :decorative="true"
        />
      </div>
    </a>
    <slot name="clear" :clear="clear"
      ><a
        v-if="allowClear"
        v-tooltip="clearText"
        role="button"
        :aria-label="clearText"
        tabindex="0"
        class="trigger-clear"
        @click.stop="clear($event, true)"
        @keydown.enter="clear($event, true)"
        @keydown.space.prevent="clear($event, true)"
        ><cat-icon icon="times-light"></cat-icon></a
    ></slot>

    <transition name="showmenu" @after-leave="putBack">
      <div
        v-show="open"
        ref="menu"
        class="cat-menu-dropdown"
        :class="_dropdownClass"
        :role="!items ? null : html ? 'navigation' : 'menu'"
        :data-label="label || current || offscreenLabel"
        @click.stop=""
        @keydown.esc="close($event, true)"
        @keydown.tab.exact="closeOnTab($event, true, false)"
        @keydown.shift.tab.exact="closeOnTab($event, true, true)"
      >
        <div class="cat-menu-arrow" data-popper-arrow></div>
        <div class="cat-menu-dropdown-inner">
          <ul
            v-if="!html"
            class="cat-menu menu-items"
            role="menu"
            tabindex="-1"
            @blur.self="close"
          >
            <li
              v-for="(item, index) in items"
              :key="item.value"
              role="presentation"
              class="cat-menu-item-wrapper"
              :lang="item.lang ? item.lang : null"
              :class="item.css"
            >
              <a
                :id="menuItemId(index)"
                :ref="menuItemId(index)"
                class="cat-menu-item"
                role="menuitem"
                tabindex="-1"
                :href="item.isLink ? item.url : null"
                :aria-label="item.description"
                :aria-describedby="
                  item.describedBy
                    ? item.describedBy
                    : describedBy
                      ? describedBy
                      : undefined
                "
                :data-label="item.text"
                :lang="item.lang ? item.lang : null"
                @click="
                  item.isLink
                    ? function () {
                        return false;
                      }
                    : select(item.value, item)
                "
                @keydown.enter.stop="
                  item.isLink
                    ? function () {
                        return false;
                      }
                    : select(item.value, item)
                "
                @keydown.space.prevent="
                  item.isLink
                    ? function () {
                        return false;
                      }
                    : select(item.value, item)
                "
                @keydown.down.prevent="
                  focusMenuItem(index === items.length - 1 ? 0 : index + 1)
                "
                @keydown.up.prevent="
                  focusMenuItem(index === 0 ? items.length - 1 : index - 1)
                "
                @keydown.home.prevent="focusMenuItem(0)"
                @keydown.end.prevent="focusMenuItem(items.length - 1)"
                @keydown.tab="close"
                @keydown.esc="close($event, true)"
                @mouseover="focusMenuItem(index)"
                ><slot name="item" v-bind="item"
                  ><span>{{ item.text }}</span></slot
                ></a
              >
            </li>
          </ul>
          <div
            v-if="html"
            :id="resolvedId + '-menuWrap'"
            class="menu-html-wrap"
            :role="!items ? 'region' : null"
            :aria-label="!items ? descriptiveLabel : null"
          >
            <div v-if="!items" class="menu-html-wrap-external">
              <slot :clear="clear" :close="close"></slot>
            </div>
            <cat-menu-items
              v-if="items"
              :depth="1"
              is-root
              :items="items"
              @ui-menu-select="selectMenu"
              @ui-menu-close="close"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script lang="ts">
import { defineComponent, inject } from "vue";
import CatMenuItems from "@/components/catMenuItems/catMenuItems";
import CatIcon from "@/components/utilities/catIcon.vue";
import { defaultStopProcessUpdate } from "@/components/utilities/processUpdateMixin";
import jQuery from "jquery";
import { createPopper } from "@popperjs/core";
import preventOverflow from "@popperjs/core/lib/modifiers/preventOverflow.js";
import { focusableElements } from "@/components/utilities/utils.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const stopProcessUpdate = inject(
      "stopProcessUpdate",
      () => defaultStopProcessUpdate
    );

    const generatedId = Math.random().toString(36).slice(2);

    return {
      stopProcessUpdate,
      generatedId
    };
  },
  name: "InlineMenu",
  components: {
    CatMenuItems,
    CatIcon
  },
  props: {
    id: {
      type: [String, Number],
      default: null,
      description: "Inline menu (catMenu) id."
    },
    menuButtonId: {
      type: String,
      default: null,
      description:
        "Attaches id to anchor link and creates connection to the label."
    },
    value: {
      type: [String, Number],
      description:
        "Determines the hierarchy of items (eg 1 is displayed as main link and will be displayed)."
    },
    items: {
      type: [Array],
      description: "Items included, displayed in the inline menu (catMenu)."
    },
    defaultTitle: {
      type: String,
      description: "The default title to display, if the value isn't found"
    },
    labelMenu: {
      type: String,
      description: "Label for the inline menu (catMenu) button."
    },
    button: {
      type: Boolean,
      default: false,
      description: "Displays inline menu (catMenu) as button."
    },
    primary: {
      type: Boolean,
      default: false,
      description: "Displays inline menu (catMenu) as primary button."
    },
    secondary: {
      type: Boolean,
      default: false,
      description: "Displays inline menu (catMenu) as secondary button."
    },
    hollow: {
      type: Boolean,
      default: false,
      description: "Displays inline menu (catMenu) as button hollow."
    },
    flat: {
      type: Boolean,
      default: false,
      description: "Displays inline menu (catMenu) as button flat."
    },
    small: {
      type: Boolean,
      default: false,
      description: "Displays inline menu (catMenu) as button small."
    },
    html: {
      type: Boolean,
      default: false,
      description: "Displays content as plain text."
    },
    label: {
      type: String,
      default: "",
      description: "Allocates label to inline menu (catMenu)."
    },
    offscreenLabel: {
      type: String,
      default: null,
      description:
        "Used by screen readers and assistive technologies to provide label and context for accessibility."
    },
    right: {
      type: Boolean,
      default: false,
      description: "Aligns item(s) of inline menu (catMenu) to the right."
    },
    parentBody: {
      type: Boolean,
      default: false,
      description: "Insert dropdown content, to the end of body element."
    },
    closeOnScroll: {
      type: Boolean,
      default: false,
      description: "Closes drop down on scroll."
    },
    fullWidth: {
      type: Boolean,
      default: false,
      description:
        "Also known as megaMenu with combination of props items and html"
    },
    dense: {
      type: Boolean,
      default: false,
      description: "Adds class 'dense' to the dropdown element"
    },
    allowClear: {
      type: Boolean,
      default: false,
      description: "Renders 'clear' button next to the menu"
    },
    fillmobile: {
      type: Boolean,
      default: false,
      description: "Adds class 'fillmobile' to the menu button element "
    },
    hideTriggerCaret: {
      type: Boolean,
      default: false,
      description: "Adds class 'hide-arrow' to the menu wrapper element "
    },
    triggerClass: {
      type: String,
      default: "",
      description: "Adds this value as a class to the menu button element"
    },
    triggerAriaLive: {
      type: String,
      default: null,
      description:
        "Aria live value of the menu button. Value can be polite or assertive."
    },
    buttonFluid: {
      type: Boolean,
      default: false,
      description: "TODO"
    },
    menuSize: {
      type: String,
      default: "medium-large",
      description: "Adds value as a class to the wrapper element"
    },
    clearText: {
      type: String,
      description:
        "Adds tool tip and aria-label to close button within drop down menu?"
    },
    icon: {
      type: String,
      description: "Renders icon {x} on left side of the button."
    },
    iconRight: {
      type: String,
      description:
        "Renders icon {x} on right side of the button and hides the default icon."
    },
    describedBy: {
      type: String,
      description:
        "For screen readers, ID of an element describing context for all the menu items, good for e.g. actions of repeaters."
    }
  },
  data: function () {
    return {
      open: false
    };
  },
  computed: {
    resolvedId: function () {
      return this.id ?? this.generatedId;
    },
    current: function () {
      if (this.value || this.value === 0) {
        for (var i in this.items) {
          if (this.items[i].value === this.value) {
            return this.items[i].text;
          }
        }
      }
      return this.defaultTitle;
    },
    buttonCss: function () {
      return {
        title: true,
        primary: this.primary,
        secondary: this.secondary,
        button: this.button,
        link: !this.button,
        flat: this.flat,
        small: this.small,
        hollow: this.hollow,
        fillmobile: this.fillmobile
      };
    },
    _class: function () {
      return {
        "cat-inline-menu": true,
        "right-shift": this.right,
        "html-content": this.html,
        opened: this.open,
        "allow-clear": this.allowClear,
        "hide-arrow": this.hideDefaultArrow,
        "menu-full-width": this.fullWidth
      };
    },
    _dropdownClass: function () {
      return {
        "full-width": this.fullWidth,
        "html-dropdown-content": this.html,
        dense: this.dense,
        "move-dropdown": this.parentBody
      };
    },
    _sizeClass: function () {
      return this.menuSize !== "" && this.menuSize != null ? this.menuSize : "";
    },
    descriptiveLabel: function () {
      var label = "";

      if (this.offscreenLabel) {
        label = this.offscreenLabel + " ";
      }

      if (this.labelMenu) {
        label = this.labelMenu + " ";
      }

      if (this.label && this.label !== "") {
        label = label + this.label;
      } else {
        label = label + this.current;
      }

      return label;
    },
    triggerElement: function () {
      return this.$refs.button;
    },
    menuElement: function () {
      return this.$refs.menu;
    },
    hideDefaultArrow: function () {
      return this.hideTriggerCaret || this.iconRight;
    },
    menuHtmlWrapId: function () {
      return this.id ? this.id : Math.random().toString(36).slice(2);
    }
  },

  mounted() {
    var _t = this;
    var thisEl = _t.$el;
    jQuery(document).on("menu.ui-open", function (ev, element) {
      if (thisEl && thisEl !== element.el) {
        _t.close();
      }
    });
  },

  beforeDestroy() {
    this.putBack();
  },
  methods: {
    onKeyDown: function (e) {
      this.$emit("ui-key-down", e);
    },
    toggle: function (event) {
      var self = this;
      var menu = jQuery(this.$refs.menu);

      this.open = !this.open;

      if (this.open && this.parentBody) {
        var pos = this.triggerElement.getBoundingClientRect();
        var styles = {
          position: "absolute",
          top:
            pos.top +
            10 +
            pos.height +
            Math.abs(jQuery("body").get(0).getBoundingClientRect().top),
          left: function () {
            if (self.fullWidth) {
              return 0;
            } else {
              return self.right ? pos.right - menu.outerWidth() : pos.left;
            }
          },
          right: function () {
            if (self.fullWidth) {
              return 0;
            } else {
              return null;
            }
          }
        };

        jQuery("body").append(menu.css(styles));
      }

      if (this.closeOnScroll) {
        jQuery(window).on("scroll.menu", function (eve) {
          self.close();
          jQuery(this).delay(10).off(eve);
        });
      }

      if (this.open) {
        var element = this.$el;
        this.$emit("ui-open", event);
        jQuery(document).trigger("menu.ui-open", { el: element });
        jQuery(document).on("click.menu", function (ev) {
          if (self.$refs.menu && !self.$refs.menu.contains(ev.target)) {
            self.close();
            jQuery(document).off(ev);
          }
        });
      }
      // Main menu and mega menu has they own handling
      // Focus on the first menu item if none is selected or the current
      this.$nextTick(function () {
        if (this.open) {
          if (!this.html) {
            this.focusCurrentItem();
          } else {
            jQuery(self.$refs.menu).find(focusableElements).first().focus();
          }
        }
      });

      if (!this.open) {
        this.close();
      }

      this.menuStyles(this.open);
      this.stopProcessUpdate("catMenu.vue name: inline-menu method: toggle");
    },
    close: function (event, focus) {
      this.open = false;
      if (focus || (event && event.focus)) {
        // Return focus to the menu button
        jQuery(".cat-menu-button", this.$el).first().focus();
      }
      this.$emit("ui-close");
    },
    closeOnTab: function (event, focus, shift) {
      // To fix Keyboard navigation issue
      // when dropdown content is moved at the end of the body
      if (this.parentBody && this.items) {
        if (shift) {
          // Prevents multiple previous tab and focus to trigger
          event.preventDefault();
        }

        this.close(event, focus);
      }
    },
    selectMenu: function (event) {
      this.select(event.value);
    },
    select: function (value, item) {
      this.open = false;
      this.$emit("ui-select", { value: value, item: item });
    },

    putBack: function () {
      if (this.close && this.parentBody) {
        jQuery(this.$refs.container).append(jQuery(this.$refs.menu));
      }
    },
    menuItemId: function (index) {
      let itemId = "";
      if (this.id) {
        itemId += this.id;
      }

      itemId += "_menu_item_" + index;
      return itemId;
    },
    focusCurrentItem: function () {
      for (var i in this.items) {
        if (this.items[i].text === this.current) {
          this.focusMenuItem(i);
          return;
        }
      }
      this.focusMenuItem(0);
    },
    focusMenuItem: function (index) {
      var self = this;
      jQuery(self.$refs[self.menuItemId(index)]).first().focus();
    },
    clear: function (e, focus) {
      this.$emit("ui-clear", e);
      this.close(e, focus);
    },
    menuStyles: function (isOpen) {
      if (isOpen && !this.fullWidth) {
        let button = this.triggerElement;
        let menu = this.menuElement;
        let popperPlacement = this.right ? "bottom-end" : "bottom-start";

        // Position dropdown to bottom centered when labelMenu exist
        if (this.labelMenu) {
          popperPlacement = "bottom";
        }

        createPopper(button, menu, {
          placement: popperPlacement,
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 8]
              }
            },
            {
              name: "preventOverflow",
              options: {
                padding: 12
              }
            },
            {
              name: "computeStyles",
              options: {
                gpuAcceleration: false, // true by default
                adaptive: false
              }
            }
          ]
        });
      }
    }
  }
});
</script>
